import {
  Alert,
  AlertColor,
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  Snackbar,
  Switch,
  Toolbar,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import * as Sentry from '@sentry/react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import {
  Awo, AwoUserMutationResponse, NewAwoForm, NewAwoFormValues,
} from '../../types/Awo';
import { beforeCapture, parseAwoCreateFormValues } from '../../utilities';
import countriesOptions from '../../resources/countriesData';
import { CREATE_AWO } from '../../queries';
import LocationPartial, { locationSchema } from '../FormPartials/LocationPartial';
import WebsitePartial, { websiteSchema } from '../FormPartials/WebsitePartial';
import NamesPartial, { namesSchema } from '../FormPartials/NamesPartial';
import Timezone, { timezoneSchema } from '../FormPartials/TimezonePartial';
import RescueTypePartial, { rescueTypeSchema } from '../FormPartials/RescueTypePartial';
import NotePartial, { noteSchema } from '../FormPartials/NotePartial';
import ShelterManagementPartial, { shelterManagementSchema } from '../FormPartials/ShelterManagementPartial';
import AnimalsPartial, { courseSelectionSchema } from '../FormPartials/AnimalsPartial';
import DonationLinkPartial, { donationLinkSchema } from '../FormPartials/DonationLinkPartial';
import PhoneNumberPartial, { phoneNumberSchema } from './PhoneNumberPartial';
import AdoptionPacketDogsPartial, { adoptionPacketDogsSchema } from '../FormPartials/AdoptionPacketDogsPartial';
import AdoptionPacketsCatsPartial, { adoptionPacketCatsSchema } from '../FormPartials/AdoptionPacketCatsPartial';
import FAQLinkPartial, { faqLinkSchema } from '../FormPartials/FAQLinkPartial';
import PetszelPartnerPartial, { petszelPartnerSchema } from '../FormPartials/PetszelPartnerPartial';
import KeyContactPartial, { keyContactsSchema, KEY_CONTACT_FIELDS } from '../FormPartials/KeyContactPartial';

const defaultValues: NewAwoForm = {
  name: '',
  displayName: '',
  smsName: null,
  token: '',
  apiUrl: '',
  apiKey: '',
  note: '',
  rescueType: null,
  website: '',
  donationLink: '',
  faqLink: '',
  orgPhoneNumber: '',
  adoptionPacketLinkDogs: '',
  adoptionPacketLinkCats: '',
  adoptAPetEnabled: false,
  country: null,
  streetAddress: '',
  city: '',
  state: null,
  postal: undefined,
  timezone: null,
  petcademyPlan: '',
  checkboxSelection: false,
  googleDriveFolder: '',
  dogAdopt: false,
  catAdopt: false,
  dogFoster: false,
  catFoster: false,
  kittenFoster: false,
  firstTeamMemberFirstName: '',
  firstTeamMemberLastName: '',
  firstTeamMemberEmail: '',
  petszelPartner: null,
  behaviorEscalationContact: '',
  fosterContact: '',
  adopterContact: '',
  preSurrenderContact: '',
  primaryAdmin: false,
};

function NewOrganizationModal({ onClose }: { onClose: (awoName?: string) => void }) {
  const [selectedCountryOption, setSelectedCountryOption] = React.useState<Record<'label' | 'value' | 'timezones' | 'states', any> | null>(null);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>('error');

  const [createAwo, { loading }] = useMutation(CREATE_AWO, {
    onCompleted: (data: { createAwo: AwoUserMutationResponse }) => {
      const response = data.createAwo;
      setSnackbarOpen(true);
      setSnackbarMessage(response.message);
      if (!response.success) {
        setSnackbarSeverity('error');
      } else {
        setSnackbarSeverity('success');
        onClose(response.awo.name);
      }
    },
    onError: (e) => {
      Sentry.withScope((scope) => {
        beforeCapture(scope, e);
        Sentry.captureException(e);
      });
      setSnackbarOpen(true);
      setSnackbarSeverity('error');
      setSnackbarMessage('Error in creating a new organization. We\'re working on it.');
    },
  });

  const schema = yup.object({
    ...namesSchema,
    ...shelterManagementSchema,
    ...rescueTypeSchema,
    ...locationSchema,
    ...timezoneSchema,
    ...noteSchema,
    ...websiteSchema,
    ...donationLinkSchema,
    ...phoneNumberSchema,
    ...adoptionPacketDogsSchema,
    ...adoptionPacketCatsSchema,
    ...courseSelectionSchema,
    ...petszelPartnerSchema,
    ...keyContactsSchema,
    firstTeamMemberFirstName: yup.string().required('First name is required'),
    firstTeamMemberLastName: yup.string().required('Last name is required'),
    firstTeamMemberEmail: yup.string().email('Must be a valid email').matches(/@[^.]*\./, 'Must be a valid email').required('Email is required'),
  }).required();

  const {
    register, control, formState, setValue, getValues, ...rest
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema) as any,
  });

  const { errors } = formState;

  useEffect(() => {
    dayjs.extend(timezone);
    const guessedCountry = (selectedCountryOption?.value) || dayjs.tz.guess().split('/')[0];
    const guessedCountryOption = countriesOptions.find((country) => country.value.includes(guessedCountry));
    if (guessedCountryOption) {
      setSelectedCountryOption(guessedCountryOption);
    }
  }, [selectedCountryOption?.value]);

  const formSubmitHandler = async (values: Required<NewAwoFormValues>) => {
    const strictValues = parseAwoCreateFormValues(values);
    await createAwo({
      variables: {
        awo: {
          ...strictValues,
        },
      },
    });
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={6000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        key={snackbarMessage}
      >
        <Alert severity={snackbarSeverity}>{snackbarMessage || 'Error in creating a new organization. We\'re working on it.'}</Alert>
      </Snackbar>
      <AppBar sx={{
        position: 'fixed',
        boxShadow: 0,
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        bgcolor: '#fff',
        color: 'primary.contrastLight',
      }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => onClose()}
            aria-label="close"
          >
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1, color: 'primary.textBlack' }} variant="h5" component="div">
            Create new organization
          </Typography>
          <Chip
            color="info"
            label="All fields are required unless marked"
          />
        </Toolbar>
      </AppBar>
      <Box px={2} pt={8} pb={6} maxWidth="800px" width="100%" mx="auto">
        <FormContainer
          formContext={{
            register, control, formState, setValue, getValues, ...rest,
          }}
          onSuccess={formSubmitHandler as any}
        >
          <Grid
            sx={{ flexWrap: { md: 'nowrap' }, padding: { xs: '20px 0px', md: '40px 0px' } }}
            columns={14}
            container
            spacing={2}
          >
            <Grid item xs={14} md={4}>
              <Typography variant="overline">
                External data link
              </Typography>
            </Grid>
            <Grid item xs={14} md={10} container direction="column" gap="40px">
              <Grid item>
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography variant="body1">
                    Shelter management software
                  </Typography>
                  <Typography variant="caption">Select the software this organization uses to manage animals. Next steps will appear below.</Typography>
                </Box>
                <ShelterManagementPartial
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  getValues={getValues}
                />
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <Grid
            sx={{ flexWrap: { md: 'nowrap' }, padding: { xs: '20px 0px', md: '40px 0px' } }}
            columns={14}
            container
            spacing={2}
          >
            <Grid xs={14} md={4} item>
              <Typography variant="overline">
                Required information
              </Typography>
            </Grid>
            <Grid xs={14} md={10} item container direction="column" gap="40px">
              <Grid item>
                <Typography variant="body1">
                  Names
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  How this organization is labeled in different situations
                </FormLabel>
                <NamesPartial />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Type
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }} id="rescues-radio-group-label">
                  Rescues
                  are typically private volunteer-run organizations, shelters are government funded
                </FormLabel>
                <RescueTypePartial control={control} errors={errors} />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Location
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Used to generate SMS number
                </FormLabel>
                <LocationPartial
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  selectedCountryOption={selectedCountryOption}
                  setSelectedCountryOption={setSelectedCountryOption}
                />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Time zone
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Select time zone used to
                  determine when emails and SMS messages are sent
                </FormLabel>
                <Timezone
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  selectedCountryOption={selectedCountryOption}
                />
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <Grid
            sx={{ flexWrap: { md: 'nowrap' }, padding: { xs: '20px 0px', md: '40px 0px' } }}
            columns={14}
            container
            spacing={2}
          >
            <Grid xs={14} md={4} item>
              <Typography variant="overline">
                Optional information
              </Typography>
            </Grid>
            <Grid xs={14} md={10} item container direction="column" gap="40px">
              <Grid item>
                <Typography variant="body1">
                  Note
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Unique details about this organization. Only Petcademy staff can see it.
                </FormLabel>
                <NotePartial />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Website
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Homepage for this organization. Ensure https:// is included in the URL.
                </FormLabel>
                <WebsitePartial />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Donation link
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Direct link to send donations to this organization. Ensure https:// is included in the URL.
                </FormLabel>
                <DonationLinkPartial />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Phone number
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  General phone number for the organization. Adopters may need this.
                </FormLabel>
                <PhoneNumberPartial
                  country={getValues('country')?.value as Awo['country'] | undefined}
                  onPhoneNumberChange={(phoneNumber) => setValue('orgPhoneNumber', phoneNumber)}
                />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Frequently asked questions
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Direct link to Google Drive or PDF. Ensure https:// is included in the URL.
                </FormLabel>
                <FAQLinkPartial />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Adoption packet for dogs
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Direct link to Google Drive or PDF. Ensure https:// is included in the URL.
                </FormLabel>
                <AdoptionPacketDogsPartial />
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  Adoption packet for cats
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Direct link to Google Drive or PDF. Ensure https:// is included in the URL.
                </FormLabel>
                <AdoptionPacketsCatsPartial />
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <Grid
            sx={{ flexWrap: { md: 'nowrap' }, padding: { xs: '20px 0px', md: '40px 0px' } }}
            columns={14}
            container
            spacing={2}
          >
            <Grid xs={14} md={4} item>
              <Typography variant="overline">
                AWO partners
              </Typography>
            </Grid>
            <Grid xs={14} md={10} item container direction="column" gap="40px">
              <Grid item>
                <Typography variant="body1">
                  Petszel partner
                </Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Whether this organization is a Petszel partner. This section will
                  expand over time to include other partners, like microchip providers.
                </FormLabel>
                <PetszelPartnerPartial control={control} errors={errors} />
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <Grid
            sx={{ flexWrap: { md: 'nowrap' }, padding: { xs: '20px 0px', md: '40px 0px' } }}
            columns={14}
            container
            spacing={2}
          >
            <Grid xs={14} md={4} item>
              <Typography variant="overline">
                Key contacts
              </Typography>
            </Grid>
            <Grid xs={14} md={10} item container direction="column" gap="40px">
              <Grid item>
                {KEY_CONTACT_FIELDS.map((field) => (
                  <KeyContactPartial
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    helperText={field.helperText}
                  />
                ))}
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <Grid
            sx={{ flexWrap: { md: 'nowrap' }, padding: { xs: '20px 0px', md: '40px 0px' } }}
            columns={14}
            container
            spacing={2}
          >
            <Grid xs={14} md={4} item>
              <Typography variant="overline">
                Animals
              </Typography>
            </Grid>
            <Grid xs={14} md={10} item container direction="column" gap="24px">
              <AnimalsPartial setValue={setValue} control={control} errors={errors} includeSubtext />
            </Grid>
          </Grid>
          <Divider />
          <Grid
            sx={{ flexWrap: { md: 'nowrap' }, padding: { xs: '20px 0px', md: '40px 0px' } }}
            columns={14}
            container
            spacing={2}
          >
            <Grid xs={14} md={4} item>
              <Typography variant="overline">
                Invite
              </Typography>
            </Grid>
            <Grid xs={14} md={10} item container direction="column" gap="40px">
              <Grid item>
                <Typography variant="body1">First team member</Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Send an invitation email to the
                  first team member for this organization. You can add additional members afterwards.
                </FormLabel>
                <TextFieldElement
                  name="firstTeamMemberFirstName"
                  sx={{ marginTop: 2 }}
                  fullWidth
                  label="First Name"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
                <TextFieldElement
                  name="firstTeamMemberLastName"
                  sx={{ marginTop: 2 }}
                  fullWidth
                  label="Last Name"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
                <TextFieldElement
                  name="firstTeamMemberEmail"
                  sx={{ marginTop: 2 }}
                  fullWidth
                  label="Email"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item>
                <Typography variant="body1">Primary admin</Typography>
                <FormLabel sx={{ fontSize: '12px' }}>
                  Set this person as the organization&apos;s primary admin. This
                  contact is synced to Customer.io for admin communication.
                </FormLabel>
                <FormGroup {...register('primaryAdmin')} sx={{ marginTop: 1 }}>
                  <FormControlLabel
                    control={<Switch name="primaryAdmin" />}
                    label="Set as primary admin"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </Grid>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              background: '#fff',
              borderTop: '1px solid #E0E0E0',
              zIndex: 2,
            }}
            py={2}
            px={6}
          >
            <Button
              type="submit"
              disabled={loading}
              variant="contained"
            >
              {loading ? 'Loading' : 'Create new organization'}
            </Button>
          </Box>
        </FormContainer>
      </Box>
    </>
  );
}

export default NewOrganizationModal;
